import type { ChatMessage, ChatParams, ModelInfo } from "@/types";

/**
 * AI service layer types.
 *
 * `AIProvider` is the seam a future backend (llama.cpp, LM Studio, a Seufic
 * runtime) implements. Nothing outside `services/ai` and `providers/index.ts`
 * should import a concrete provider directly.
 */

export type AIServiceErrorKind =
  | "network"
  | "timeout"
  | "aborted"
  | "model-not-found"
  | "invalid-response"
  | "unknown";

export class AIServiceError extends Error {
  readonly kind: AIServiceErrorKind;

  constructor(kind: AIServiceErrorKind, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "AIServiceError";
    this.kind = kind;
  }
}

/** A single streamed piece of an assistant reply. */
export interface ChatStreamDelta {
  content: string;
  done: boolean;
}

export interface StreamChatRequest {
  baseUrl: string;
  model: string;
  messages: ChatMessage[];
  params: ChatParams;
  signal?: AbortSignal;
}

/** The contract every inference backend implements. */
export interface AIProvider {
  listModels(baseUrl: string): Promise<ModelInfo[]>;
  streamChat(request: StreamChatRequest): AsyncGenerator<ChatStreamDelta>;
  /** Best-effort short title from a seed message; `null` on any failure. */
  generateTitle(baseUrl: string, model: string, seedText: string): Promise<string | null>;
}
